import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "../components/AppLayout";
import { api, formatApiError } from "../lib/api";
import { Users, Syringe, MapPin, Star, Shield, Sparkles, Flame, Zap, Heart } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

const REACTIONS = [
  { kind: "fire", icon: Flame, label: "Fire" },
  { kind: "lightning", icon: Zap, label: "Charged" },
  { kind: "heart", icon: Heart, label: "Heart" },
];

function ReactionRow({ item, onChange }) {
  const [busy, setBusy] = useState(false);
  const react = async (kind) => {
    if (busy) return;
    setBusy(true);
    const currently = item.viewer_reaction;
    try {
      if (currently === kind) {
        const { data } = await api.delete(`/shots/${item.id}/react`);
        onChange(item.id, data.viewer_reaction, data.counts);
      } else {
        const { data } = await api.post(`/shots/${item.id}/react`, { kind });
        onChange(item.id, data.viewer_reaction, data.counts);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-1.5 mt-2" data-testid={`feed-reactions-${item.id}`}>
      {REACTIONS.map(({ kind, icon: Icon, label }) => {
        const active = item.viewer_reaction === kind;
        const count = item.reactions?.[kind] || 0;
        return (
          <button
            key={kind}
            type="button"
            onClick={() => react(kind)}
            disabled={busy}
            aria-pressed={active}
            title={label}
            data-testid={`feed-react-${kind}-${item.id}`}
            className={`inline-flex items-center gap-1 h-7 px-2 border text-[11px] font-mono-num uppercase tracking-widest transition-colors disabled:opacity-50 ${
              active
                ? "border-[#0D9488] bg-[#0D9488]/10 text-[#0D9488]"
                : "border-slate-200 text-slate-500 hover:border-[#0D9488]/50 hover:text-[#0D9488]"
            }`}
          >
            <Icon className={`w-3 h-3 ${active && kind === "heart" ? "fill-[#0D9488]" : ""}`} />
            {count > 0 && <span className="tabular-nums">{count}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default function Feed() {
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const updateReaction = useCallback((shotId, viewerReaction, counts) => {
    setItems((cur) => cur.map((it) => it.id === shotId ? { ...it, viewer_reaction: viewerReaction, reactions: counts || {} } : it));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/feed/following");
        if (cancelled) return;
        setItems(data.items || []);
        setCount(data.following_count || 0);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <AppLayout>
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-2">
          <Users className="w-4 h-4 text-[#0D9488]" />
          <div className="font-mono-num text-[11px] text-[#0D9488] uppercase tracking-[0.3em]">// Following</div>
        </div>
        <h1 className="font-display font-black uppercase tracking-tight text-5xl lg:text-6xl leading-none">Feed</h1>
        <p className="text-slate-500 mt-3 text-sm">
          Recent shots from the {count} {count === 1 ? "person" : "people"} you follow.
        </p>
      </div>

      {loading ? (
        <div className="border border-slate-200 bg-white p-10 text-slate-500 text-sm animate-pulse" data-testid="feed-loading">Loading feed…</div>
      ) : count === 0 ? (
        <div className="border border-slate-200 bg-white p-10 text-center" data-testid="feed-empty">
          <Sparkles className="w-6 h-6 text-[#0D9488] mx-auto mb-3" />
          <div className="font-display font-black uppercase tracking-tight text-2xl mb-2">You're not following anyone yet.</div>
          <p className="text-slate-600 text-sm max-w-md mx-auto mb-5">
            Open any teammate's profile from the Community, then tap Follow — their doses will show up here.
          </p>
          <Link
            to="/community"
            className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] text-black px-5 h-10 text-xs uppercase tracking-widest font-semibold"
            data-testid="feed-empty-cta"
          >
            <Users className="w-3.5 h-3.5" /> Browse Community
          </Link>
        </div>
      ) : items.length === 0 ? (
        <div className="border border-slate-200 bg-white p-10 text-center text-slate-600 text-sm" data-testid="feed-quiet">
          Nothing new from the people you follow yet. Check back after they log their next dose.
        </div>
      ) : (
        <ul className="border border-slate-200 divide-y divide-zinc-900 bg-white" data-testid="feed-list">
          {items.map((it) => {
            const initials = (it.user_name || "?").split(" ").slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();
            const profileKey = it.user_handle || it.user_id;
            return (
              <li key={it.id} className="px-4 py-4 flex items-start gap-3" data-testid={`feed-item-${it.id}`}>
                <Link to={`/u/${profileKey}`} className="w-10 h-10 border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0">
                  {it.user_avatar_url ? (
                    <img src={it.user_avatar_url} alt={it.user_name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="font-display font-black text-sm text-slate-500">{initials}</span>
                  )}
                </Link>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Link to={`/u/${profileKey}`} className="font-display font-black uppercase text-sm text-slate-900 hover:text-[#0D9488] transition-colors truncate">
                      {it.user_name}
                    </Link>
                    {it.user_has_premium && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono-num uppercase tracking-widest text-black bg-[#0D9488] px-1.5 py-0.5"><Star className="w-2.5 h-2.5 fill-black" /> Premium</span>
                    )}
                    {(it.user_role === "admin" || it.user_role === "moderator") && (
                      <span className="inline-flex items-center gap-1 text-[9px] font-mono-num uppercase tracking-widest border border-[#0D9488]/50 text-[#0D9488] px-1.5 py-0.5"><Shield className="w-2.5 h-2.5" /> {it.user_role}</span>
                    )}
                    <span className="text-[10px] font-mono-num text-slate-400 uppercase tracking-widest ml-auto tabular-nums">{timeAgo(it.taken_at)}</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-800">
                    <span className="flex items-center gap-1.5"><Syringe className="w-3.5 h-3.5 text-[#0D9488]" />{it.peptide_name}</span>
                    <span className="font-mono-num text-slate-600 tabular-nums">
                      {it.dose_amount ?? "?"} {it.dose_unit || ""}
                    </span>
                    {it.injection_site && (
                      <span className="flex items-center gap-1 font-mono-num text-[11px] text-slate-500 uppercase tracking-widest">
                        <MapPin className="w-3 h-3" /> {it.injection_site}
                      </span>
                    )}
                  </div>
                  <ReactionRow item={it} onChange={updateReaction} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </AppLayout>
  );
}
